import axios from 'axios';
import jwt_decode from '../lib/jwt';

// Configuración según documentación
const API_BASE_URL = 'https://talentos-services.xn--am-yja.org/api';  

// Función auxiliar para obtener el ID del usuario desde el JWT
const getCurrentUserId = () => {
    try {
        const token = localStorage.getItem('atfgscomm');
        if (!token) return null;
        
        const payload = jwt_decode(token);
        // Intentar diferentes campos comunes para el ID del usuario
        return payload.sub || payload.userId || payload.id || payload._id || payload.user_id || null;
    } catch (error) {
        console.error('Error al decodificar token para obtener userId:', error);
        return null;
    }
};

// Función auxiliar para normalizar el claimLink (corrige URLs mal formadas)
const normalizeClaimLink = (claimLink, code) => {
    if (!claimLink && !code) return null;
    
    // Si no hay claimLink pero hay código, construir la URL
    if (!claimLink && code) {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        return `${origin}/claim/${code}`;
    }
    
    // Si el claimLink está mal formado (tiene http://http//), corregirlo
    if (claimLink && claimLink.includes('http://http//') || claimLink.includes('https://https//')) {
        // Extraer el código del path
        const codeMatch = claimLink.match(/\/claim\/([^\/\s]+)/);
        const extractedCode = codeMatch ? codeMatch[1] : code;
        
        if (extractedCode) {
            const origin = typeof window !== 'undefined' ? window.location.origin : '';
            return `${origin}/claim/${extractedCode}`;
        }
    }
    
    // Si el claimLink es solo un path (empieza con /), agregar el origin
    if (claimLink && claimLink.startsWith('/')) {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        return `${origin}${claimLink}`;
    }
    
    // Si ya es una URL válida, retornarla tal cual
    if (claimLink && (claimLink.startsWith('http://') || claimLink.startsWith('https://'))) {
        // Verificar que no tenga doble http
        const cleaned = claimLink.replace(/^https?:\/\/https?:\/\//, 'http://').replace(/^https?:\/\/http:\/\//, 'http://');
        return cleaned;
    }
    
    return claimLink;
};

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Interceptor para agregar API Key y JWT Token a todas las peticiones (como en talentos.js)
api.interceptors.request.use((config) => {
    // Usar la API Key de la variable de entorno o la key por defecto
    const apiKey = process.env.REACT_APP_API_KEY || 'tal_internal_practices_service_5604f8e446adefc0876b5e827760cb547cb531b9787e6bc73d3af39595174897';

    if (apiKey) {
        config.headers['X-API-Key'] = apiKey;
    } else {
        console.warn('⚠️ No API Key found!');
    }

    // Agregar JWT Token si está disponible (necesario para algunos endpoints como applications)
    const jwtToken = localStorage.getItem('atfgscomm');
    if (jwtToken) {
        config.headers['Authorization'] = `Bearer ${jwtToken}`;
    }

    return config;
}, (error) => {
    console.error('Error en interceptor de request:', error);
    return Promise.reject(error);
});

// Configuración de headers según documentación (para uso manual si es necesario)
export const getAuthHeaders = (useApiKey = false) => {
    if (useApiKey) {
        return { 'X-API-Key': process.env.REACT_APP_API_KEY }
    }
    return {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
    }
}

// Funciones según documentación (usando axios con interceptor automático)
export const checkEligibility = async (employerId) => {
    return new Promise((resolve, reject) => {
        api.get(`/credit-codes/eligibility/${employerId}`)
            .then(res => {
                const data = res.data;
                resolve({
                    eligible: data.eligible,
                    reason: data.reason,
                    nextDate: data.nextAvailableDate,
                    daysLeft: data.daysUntilNext
                });
            })
            .catch(err => {
                console.error('Error checking eligibility:', err);
                // Si hay error de conexión, devolver respuesta por defecto
                if (!err.response) {
                    resolve({ eligible: false, reason: 'Error de conexión' });
                } else {
                    reject(err);
                }
            });
    });
}

export const generateCreditCode = async (employerId, credits, options = {}) => {
    return new Promise((resolve, reject) => {
        // Obtener el ID del usuario actual desde el JWT
        const createdBy = getCurrentUserId();
        
        if (!createdBy) {
            reject(new Error('No se pudo obtener el ID del usuario actual. Por favor, inicia sesión nuevamente.'));
            return;
        }

        const data = {
            credits,
            assignedToUserId: employerId,
            createdBy: createdBy, // Agregar el campo requerido
            description: options.description || '',
            expiresAt: options.expiresAt,
            maxUses: options.maxUses || 1
        };

        api.post('/credit-codes/generate', data)
            .then(res => {
                const result = res.data;

                // Normalizar el claimLink (corregir URLs mal formadas)
                const normalizedLink = normalizeClaimLink(result.claimLink, result.code);

                // Copiar enlace normalizado al portapapeles
                if (normalizedLink) {
                    navigator.clipboard.writeText(normalizedLink).catch(() => {
                        // Silenciar error si no se puede copiar
                    });
                }

                resolve({
                    code: result.code,
                    link: normalizedLink || result.claimLink,
                    claimLink: normalizedLink || result.claimLink, // Asegurar que claimLink esté normalizado
                    credits: result.credits,
                    expiresAt: result.expiresAt
                });
            })
            .catch(err => {
                console.error('Error generating code:', err);
                reject(err);
            });
    });
}

export const fetchCreditCodes = async (filters = {}) => {
    return new Promise((resolve, reject) => {
        api.get('/credit-codes', { params: filters })
            .then(res => {
                const data = res.data;
                const codes = data.codes || data;
                
                // Normalizar claimLinks de todos los códigos
                if (Array.isArray(codes)) {
                    codes.forEach(code => {
                        if (code.claimLink) {
                            code.claimLink = normalizeClaimLink(code.claimLink, code.code);
                        }
                    });
                }
                
                resolve({
                    codes: codes,
                    pagination: data.pagination,
                    filters: data.filters
                });
            })
            .catch(err => {
                console.error('Error fetching codes:', err);
                reject(err);
            });
    });
}

export const fetchCreditStats = async (period = 'month') => {
    return new Promise((resolve, reject) => {
        api.get('/credit-codes/stats', { params: { period } })
            .then(res => resolve(res.data))
            .catch(err => {
                console.error('Error fetching stats:', err);
                reject(err);
            });
    });
}

export const getCodeById = async (code) => {
    return new Promise((resolve, reject) => {
        api.get(`/credit-codes/${code}`)
            .then(res => {
                const data = res.data;
                // Normalizar el claimLink si existe
                if (data.claimLink) {
                    data.claimLink = normalizeClaimLink(data.claimLink, data.code || code);
                }
                resolve(data);
            })
            .catch(err => {
                console.error('Error fetching code by ID:', err);
                reject(err);
            });
    });
}

export const updateCodeStatus = async (codeId, newStatus) => {
    return new Promise((resolve, reject) => {
        api.patch(`/credit-codes/${codeId}/status`, { status: newStatus })
            .then(res => resolve(res.data))
            .catch(err => {
                console.error('Error updating code status:', err);
                reject(err);
            });
    });
}

export const deleteCode = async (codeId) => {
    return new Promise((resolve, reject) => {
        api.delete(`/credit-codes/${codeId}`)
            .then(res => resolve(res.data))
            .catch(err => {
                console.error('Error deleting code:', err);
                reject(err);
            });
    });
}

export const claimCreditCode = async (code) => {
    return new Promise((resolve, reject) => {
        api.post('/credit-codes/claim', { code })
            .then(res => resolve(res.data))
            .catch(err => {
                console.error('Error claiming code:', err);
                reject(err);
            });
    });
}

// Versión pública del claim (sin autenticación JWT, solo API Key)
// El backend debería obtener el userId del código mismo (assignedToUserId)
export const claimCreditCodePublic = async (code) => {
    return new Promise((resolve, reject) => {
        // Crear una petición sin JWT token, solo con API Key
        const apiKey = process.env.REACT_APP_API_KEY || 'tal_internal_practices_service_5604f8e446adefc0876b5e827760cb547cb531b9787e6bc73d3af39595174897';
        // Usar la misma URL base que el resto de la aplicación
        const baseUrl = process.env.REACT_APP_API_BASE_URL || API_BASE_URL;
        
        // Verificar que la API key existe
        if (!apiKey) {
            reject(new Error('API Key no configurada'));
            return;
        }
        
        // Headers explícitos - NO incluir Authorization, SÍ incluir X-API-Key
        const headers = {
            'Content-Type': 'application/json',
            'X-API-Key': apiKey
        };
        
        // Body solo con code - el backend debería obtener el userId del código (assignedToUserId)
        const body = {
            code
        };
        
        // Logs detallados para verificar que la API key se envía
        console.log('🔓 Claim público - Configuración:', {
            baseUrl,
            code,
            hasApiKey: !!apiKey,
            apiKeyLength: apiKey.length,
            apiKeyPreview: apiKey.substring(0, 10) + '...' + apiKey.substring(apiKey.length - 10),
            headers: { ...headers, 'X-API-Key': '***' + apiKey.substring(apiKey.length - 4) },
            body: body
        });
        
        fetch(`${baseUrl}/credit-codes/claim`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(body),
            credentials: 'omit' // No enviar cookies ni credenciales
        })
        .then(async (res) => {
            const responseText = await res.text();
            
            // Verificar si hay problemas de CORS o headers
            const responseHeaders = Object.fromEntries(res.headers.entries());
            console.log('📥 Respuesta del servidor:', {
                status: res.status,
                statusText: res.statusText,
                headers: responseHeaders,
                body: responseText,
                url: `${baseUrl}/credit-codes/claim`
            });
            
            // Verificar si el error es por falta de API key
            if (res.status === 401 || res.status === 403) {
                console.warn('⚠️ Error de autenticación. Verificando si la API key se envió correctamente...');
                console.log('📤 Headers enviados:', headers);
            }
            
            if (!res.ok) {
                let errorData;
                try {
                    errorData = JSON.parse(responseText);
                } catch {
                    errorData = { error: responseText || 'Error al reclamar código' };
                }
                
                // Log detallado del error
                console.error('❌ Error en claim:', {
                    status: res.status,
                    statusText: res.statusText,
                    error: errorData,
                    requestHeaders: headers,
                    requestBody: body,
                    apiKeySent: !!apiKey,
                    apiKeyLength: apiKey ? apiKey.length : 0,
                    responseBody: responseText
                });
                
                // Mensaje más específico según el tipo de error
                if (res.status === 401) {
                    throw new Error(`Error de autenticación (401): ${errorData.error || errorData.message || 'El backend requiere autenticación. Verifica que la API Key se esté enviando correctamente.'}`);
                } else if (res.status === 403) {
                    throw new Error(`Error de autorización (403): ${errorData.error || errorData.message || 'No tienes permisos para realizar esta acción.'}`);
                } else if (res.status === 400) {
                    throw new Error(`Error de validación (400): ${errorData.error || errorData.message || 'Los datos enviados no son válidos.'}`);
                } else if (res.status === 500) {
                    // Error interno del servidor - mostrar más detalles
                    const errorMessage = errorData.error || errorData.message || errorData.details || 'Error interno del servidor';
                    throw new Error(`Error interno del servidor (500): ${errorMessage}. Por favor, contacta al administrador o intenta más tarde.`);
                }
                
                throw new Error(errorData.error || errorData.message || `Error al reclamar código (${res.status})`);
            }
            
            try {
                const data = JSON.parse(responseText);
                return data;
            } catch {
                throw new Error('Respuesta inválida del servidor');
            }
        })
        .then(data => {
            console.log('✅ Claim exitoso:', data);
            resolve(data);
        })
        .catch(err => {
            console.error('❌ Error claiming code (public):', err);
            reject(err);
        });
    });
}
